import React, { useEffect, useState } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';

/** Where every beta report lands. Admin only. */
export default function FeedbackPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [shotFor, setShotFor] = useState(null);
  const [shotSrc, setShotSrc] = useState(null);

  const load = async (status, kind) => {
    setLoading(true);
    try {
      const qs = [status ? `status=${status}` : null, kind ? `type=${kind}` : null].filter(Boolean).join('&');
      const r = await api.get('/feedback' + (qs ? `?${qs}` : ''));
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setRows([]); }
    setLoading(false);
  };

  useEffect(() => { load(filter, kindFilter); }, [filter, kindFilter]);

  const openShot = async (id) => {
    try {
      const r = await api.get(`/feedback/${id}/shot`, { responseType: 'blob' });
      setShotSrc(URL.createObjectURL(r.data));
      setShotFor(id);
    } catch (e) { /* no screenshot */ }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/feedback/${id}`, { status });
      setRows(rs => rs.map(r => r.id === id ? { ...r, status } : r));
    } catch (e) { /* ignore */ }
  };

  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const chip = (s) => ({
    new: { bg: '#FDF3DC', fg: '#9A6A0B' },
    open: { bg: '#E7EEF6', fg: '#2B5478' },
    fixed: { bg: '#E4F0E4', fg: '#2C5A2C' },
    wontfix: { bg: '#F0EDE6', fg: '#6B6660' },
  }[s] || { bg: '#F0EDE6', fg: '#6B6660' });

  return (
    <div>
      <TopBar title="Beta feedback" subtitle="Every report, newest first" />
      <div className="container" style={{ paddingBottom: 60 }}>

        <div style={{ display: 'flex', gap: 8, margin: '12px 0 16px', flexWrap: 'wrap' }}>
          {['', 'new', 'open', 'fixed', 'wontfix'].map(s => (
            <button key={s || 'all'}
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(s)}>
              {s === '' ? `All (${rows.length})` : `${s} (${counts[s] || 0})`}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, margin: '0 0 16px', flexWrap: 'wrap' }}>
          {[['', 'All types'], ['bug', 'Broke'], ['confusing', 'Confusing'],
            ['request', 'Requests'], ['missing', 'Missing'], ['other', 'Other']].map(([v, lbl]) => (
            <button key={v || 'any'}
              className={`btn btn-sm ${kindFilter === v ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setKindFilter(v)}>{lbl}</button>
          ))}
        </div>

        {loading && <div className="muted">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="card"><div className="muted">Nothing reported yet.</div></div>
        )}

        {rows.map(r => {
          const c = chip(r.status);
          const who = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || 'Unknown';
          return (
            <div key={r.id} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <strong>{who}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {new Date(r.created_at).toLocaleString()} · {r.page_title || r.page_url}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <span style={{ background: '#F0EDE6', color: '#6B6660', borderRadius: 6,
                                 padding: '3px 9px', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {({ bug: 'broke', confusing: 'confusing', request: 'request',
                        missing: 'missing', other: 'other' })[r.report_type] || r.report_type || 'bug'}
                  </span>
                  <span style={{ background: c.bg, color: c.fg, borderRadius: 6,
                                 padding: '3px 9px', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {r.status}
                  </span>
                </div>
              </div>

              <p style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{r.what_happened}</p>
              {r.expected && (
                <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                  Expected: {r.expected}
                </p>
              )}

              <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                {r.page_url} · {r.viewport} · {(r.user_agent || '').slice(0, 70)}
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {r.screenshot && (
                  <button className="btn btn-secondary btn-sm" onClick={() => openShot(r.id)}>
                    View screenshot
                  </button>
                )}
                {['open', 'fixed', 'wontfix'].map(s => (
                  <button key={s} className="btn btn-secondary btn-sm"
                          onClick={() => setStatus(r.id, s)}>Mark {s}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {shotFor && shotSrc && (
        <div onClick={() => { setShotFor(null); URL.revokeObjectURL(shotSrc); setShotSrc(null); }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(30,3,17,.8)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <img src={shotSrc} alt="Report screenshot"
               style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
